import { Plus } from "lucide-react";
import { useCallback, useState } from "react";

import { LayoutProvider } from "@/components/common/layout/layout-provider";
import { PageHeader } from "@/components/common/page-header";
import { Button } from "@/components/ui/button";
import { DateRangePicker } from "@/components/common/date-range-picker";
import { SaleCheckoutDialog } from "@/components/sales/sale-checkout-dialog";
import { SaleInvoiceDialog } from "@/components/sales/sale-invoice-dialog";
import { WalkInSaleDialog } from "@/components/sales/walk-in-sale-dialog";
import SalesTable from "@/components/sales/sales-table";
import { useSalonToday } from "@/hooks/me/use-salon";
import type { Sale } from "@/types/sales/api-types/sale-types";
import { toIsoDate } from "@/lib/utils";

export function SalesPage() {
  const today = useSalonToday();
  const [range, setRange] = useState<{ from: Date; to: Date }>({
    from: today,
    to: today,
  });
  const [walkInOpen, setWalkInOpen] = useState(false);
  const [selectedSale, setSelectedSale] = useState<Sale | null>(null);

  const dateFrom = toIsoDate(range.from);
  const dateTo = toIsoDate(range.to);

  const handleSaleClick = useCallback((sale: Sale) => {
    setSelectedSale(sale);
  }, []);

  const handleSaleDialogOpenChange = (open: boolean) => {
    if (!open) setSelectedSale(null);
  };

  return (
    <div>
      <PageHeader title="Sales" />
      <LayoutProvider>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-4">
          <DateRangePicker
            id="sales-date-range"
            from={range.from}
            to={range.to}
            onRangeChange={setRange}
          />
          <Button onClick={() => setWalkInOpen(true)}>
            <Plus />
            Walk in
          </Button>
        </div>
        <SalesTable
          key={`${dateFrom}-${dateTo}`}
          dateFrom={dateFrom}
          dateTo={dateTo}
          onSaleClick={handleSaleClick}
        />
        <WalkInSaleDialog open={walkInOpen} onOpenChange={setWalkInOpen} />
        {selectedSale && selectedSale.status === "completed" && (
          <SaleInvoiceDialog
            sale={selectedSale}
            open
            onOpenChange={handleSaleDialogOpenChange}
          />
        )}
        {selectedSale && selectedSale.status !== "completed" && (
          <SaleCheckoutDialog
            sale={selectedSale}
            open
            onOpenChange={handleSaleDialogOpenChange}
          />
        )}
      </LayoutProvider>
    </div>
  );
}
